/**
 * Generic line-items table for transaction detail pages. Shared shape
 * across invoice / SO / CS / DO / PO / Purchase / SR — line columns
 * vary slightly per type so callers pass the column config.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { money } from '../lib/format.ts';

export interface LineColumn<T> {
  label: string;
  className?: string;
  render: (line: T) => ReactNode;
}

interface Props<T> {
  lines: T[];
  columns: LineColumn<T>[];
  emptyHint?: string;
}

export function TransactionLineTable<T>({ lines, columns, emptyHint }: Props<T>) {
  if (lines.length === 0) {
    return (
      <div className="empty">
        {emptyHint ?? (
          <>
            No lines on file. Run <code>--mode L --include-all</code> on the
            agent to fetch <code>items_data</code> from OBM.
          </>
        )}
      </div>
    );
  }
  return (
    <table className="table">
      <thead>
        <tr>
          {columns.map((c, i) => (
            <th key={i} className={c.className}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lines.map((line, idx) => (
          <tr key={idx}>
            {columns.map((c, ci) => (
              <td key={ci} className={c.className}>
                {c.render(line)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Helper: SKU cell that links to the SKU page when a code is present. */
export function SkuCell({ code }: { code: string | null }) {
  if (!code) return <>—</>;
  return (
    <Link to={`/skus/${encodeURIComponent(code)}`} className="mono">
      {code}
    </Link>
  );
}

/** Helper: money cell using the parent transaction's currency. */
export function MoneyCell({
  amount,
  currency,
}: {
  amount: number;
  currency: string;
}) {
  return <>{money(amount, currency)}</>;
}
